<script setup lang="ts">
import type { RouteObject } from "@shopware/composables";
import type { NuxtError } from "#app";
import {
  clearPageRecoveryAttempt,
  hasPageRecoveryAttempted,
  markPageRecoveryAttempted,
  refreshContextSilently,
} from "~/composables/usePageRecovery";

defineOptions({
  name: "ErrorPage",
});

const props = defineProps<{
  error: NuxtError<{
    statusCode: number;
    statusMessage: string;
    message: string;
  }>;
}>();

const route = useRoute();
const { t } = useI18n();

const isMaintenanceMode = computed(() => {
  return props.error.statusMessage === "MAINTENANCE_MODE";
});

const isServerError = computed(() => {
  return typeof props.error.statusCode === "number" && props.error.statusCode >= 500;
});

const showManualRefreshMessage = ref(false);
const isAutoRetrying = ref(false);

let isFormattedError = true;
let errMessage = "";
let linkFormatter = (path: string | RouteObject) => path;

try {
  const localePath = useLocalePath();
  const { formatLink } = useInternationalization(localePath);
  linkFormatter = formatLink;

  const errorMessageMap: { [key: number]: string } = {
    404: t("errorPages.404"),
    408: t("errorPages.408"),
    500: t("errorPages.500"),
    502: t("errorPages.502"),
    503: t("errorPages.503"),
  };

  errMessage =
    errorMessageMap[props.error.statusCode as keyof typeof errorMessageMap] ||
    props.error.message;

  console.warn("[error.vue]:", props.error?.message);

  if (props.error.statusCode === 412) {
    setTimeout(() => {
      console.warn(
        "Looks like your API connection is not working. Check your _nuxt.config.ts_ configuration (shopware.endpoint and shopware.accessToken). 🤞"
      );
      console.warn(
        "For more help ➡️  https://frontends.shopware.com/resources/troubleshooting.html"
      );
    }, 2.0 * 1000);
  }
} catch (e) {
  console.error("Problem with loading error page", e);
  isFormattedError = false;
}

const statusCode = isFormattedError ? props.error.statusCode : "Error";
const errorMessage = isFormattedError ? errMessage : props.error.message;

function showManualRefreshPrompt() {
  showManualRefreshMessage.value = true;
  alert(t("errors.message-403-ajax"));
}

async function tryAgain() {
  clearPageRecoveryAttempt(route.fullPath);
  isAutoRetrying.value = true;

  try {
    await refreshContextSilently();
    markPageRecoveryAttempted(route.fullPath);
    window.location.reload();
  } catch {
    markPageRecoveryAttempted(route.fullPath);
    isAutoRetrying.value = false;
    showManualRefreshPrompt();
  }
}

function reloadPage() {
  clearPageRecoveryAttempt(route.fullPath);
  window.location.reload();
}

onMounted(async () => {
  if (!isServerError.value || !import.meta.client) return;

  if (hasPageRecoveryAttempted(route.fullPath)) {
    showManualRefreshPrompt();
    return;
  }

  isAutoRetrying.value = true;

  try {
    await refreshContextSilently();
    markPageRecoveryAttempted(route.fullPath);
    window.location.reload();
  } catch (err) {
    console.warn("[error.vue] auto-retry failed:", err);
    markPageRecoveryAttempted(route.fullPath);
    isAutoRetrying.value = false;
    showManualRefreshPrompt();
  }
});
</script>

<template>
  <section
    class="relative isolate min-h-[min(85vh,900px)] w-full overflow-hidden mt-6 container"
    aria-labelledby="page-not-found-heading"
  >
    <ErrorsMaintainMode v-if="isMaintenanceMode" />
    <template v-else>
      <!-- Full-bleed background -->
      <img
        src="~/assets/images/404-page.webp"
        width="1920"
        height="1080"
        class="absolute inset-0 h-full w-full object-cover object-center [transform:scaleX(-1)]"
        format="webp"
        loading="eager"
        :alt="$t('pageNotFound.header')"
        role="presentation"
      />
      <!-- Readability: soft white panel on the left (like the reference) -->
      <div
        class="pointer-events-none absolute inset-0 bg-gradient-to-r from-white via-white/92 to-white/25 md:via-white/88 lg:max-w-[58%] lg:from-white lg:via-white/95 lg:to-transparent"
        aria-hidden="true"
      />
      <!-- Right side dissolve -->
      <div
        class="pointer-events-none absolute inset-y-0 right-0 w-20 sm:w-28 md:w-36 lg:w-48 bg-gradient-to-l from-white/100 via-white/10 to-transparent"
        aria-hidden="true"
      />
      <!-- Blend with header: soft fade at top -->
      <div
        class="pointer-events-none absolute inset-x-0 top-0 h-20 md:h-24 lg:h-28 bg-gradient-to-b from-white via-white/75 to-transparent"
        aria-hidden="true"
      />
      <!-- Blend with footer: soft fade at bottom -->
      <div
        class="pointer-events-none absolute inset-x-0 bottom-0 h-24 md:h-28 lg:h-36 bg-gradient-to-t from-white via-white/80 to-transparent"
        aria-hidden="true"
      />
      <div
        class="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/10 to-transparent lg:hidden"
        aria-hidden="true"
      />

      <!-- Content -->
      <div
        class="relative z-10 mx-auto flex min-h-[min(85vh,900px)] max-w-7xl flex-col justify-center px-5 pt-14 pb-0 sm:px-8 md:px-12 md:pt-16 lg:max-w-none lg:px-16 lg:pt-20"
      >
        <div class="max-w-lg md:max-w-xl">
          <!-- 404 with “0” as sad face -->
          <div
            class="mb-6 flex items-center gap-1 font-sans text-[clamp(4rem,12vw,7rem)] font-bold leading-none tracking-tight text-grey-900"
            aria-hidden="true"
          >
            {{ statusCode }}
          </div>

          <p class="sr-only">{{ $t("pageNotFound.header") }}</p>

          <h1
            id="page-not-found-heading"
            class="font-marcellus text-3xl font-bold leading-tight text-grey-900 sm:text-4xl md:text-[2.75rem] md:leading-snug"
          >
            {{ $t("pageNotFound.oopsTitle") }}
          </h1>

          <p class="mt-5 text-base leading-relaxed text-grey-600 sm:text-lg">
            {{ errorMessage }}
          </p>

          <p
            v-if="isAutoRetrying"
            class="mt-4 text-base text-grey-600"
            role="status"
          >
            {{ $t("errorPages.tryAgain") }}...
          </p>

          <p
            v-if="showManualRefreshMessage"
            class="mt-4 text-base text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-4 py-3"
            role="alert"
          >
            {{ $t("errors.message-403-ajax") }}
          </p>

          <!-- Single CTA: home only (no search) -->
          <NuxtLink
            class="btn btn-primary mt-10"
            :to="isFormattedError ? linkFormatter(`/`) : `/`"
          >
            {{ $t("backToHomepage") }}
          </NuxtLink>

          <!-- Recovery actions for server errors -->
          <div v-if="isServerError && !isAutoRetrying" class="flex flex-wrap gap-3 mt-4">
            <button
              class="btn btn-secondary"
              @click="tryAgain"
            >
              {{ $t("errorPages.tryAgain") }}
            </button>
            <button
              class="btn btn-secondary"
              @click="reloadPage"
            >
              {{ $t("errorPages.reloadPage") }}
            </button>
          </div>
        </div>
      </div>
    </template>
  </section>
</template>
